/** Server-paged saas-auth tenant roster with a slug-keyed wsr-api ops overlay. */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { AppSwitch } from '@/components/ui';
import { useSetNavBadge } from '@/hooks/useNavBadges';
import type { TenantDto, TenantStatus } from '@/api/saas/types';
import { useTenantRoster } from './tenants/useTenantRoster';
import { useTenantIdentityRoster } from './tenants/useTenantIdentityRoster';
import { TenantsTable, type TenantRosterRow } from './tenants/TenantsTable';
import { CreateTenantDialog } from './tenants/CreateTenantDialog';

type StatusFilter = 'ALL' | Extract<TenantStatus, 'ACTIVE' | 'SUSPENDED'>;

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 250;

export function Tenants() {
  const { t } = useCopy();
  const {
    tenants: opsTenants,
    loading: opsLoading,
    error: opsError,
    refetch: refetchOps,
    globalEnabled,
    globalToggling,
    toggleGlobal,
  } = useTenantRoster();
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [actionFilterOn, setActionFilterOn] = useState(false);
  const {
    result: identityPage,
    loading: identityLoading,
    error: identityError,
    refetch: refetchIdentity,
  } = useTenantIdentityRoster(
    page,
    PAGE_SIZE,
    query,
    statusFilter === 'ALL' ? undefined : statusFilter,
  );
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createNote, setCreateNote] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useSetNavBadge('tenants', identityPage?.totalElements ?? null);

  const opsBySlug = useMemo(() => {
    const map = new Map(opsTenants?.map((tenant) => [tenant.tenantSlug, tenant]) ?? []);
    return map;
  }, [opsTenants]);

  const pageRows = useMemo<TenantRosterRow[]>(() => {
    const identities = identityPage?.content ?? [];
    return identities.map((identity) => ({ identity, ops: opsBySlug.get(identity.slug) }));
  }, [identityPage, opsBySlug]);

  /**
   * Handoff §5: hide the control entirely when no row on this page carries
   * `needsAttention` at all (pre-WSRAPI-0083) — an enabled filter that always
   * yields nothing is worse than no filter. This checks presence of the field,
   * not its value, so a page of all-`false` rows still shows the control.
   */
  const hasNeedsAttentionField = useMemo(
    () => pageRows.some((row) => row.ops?.needsAttention !== undefined),
    [pageRows],
  );

  const rows = useMemo<TenantRosterRow[]>(() => {
    return (
      pageRows
        // §5: strict `=== true` — `undefined` is not a match, so a
        // pre-WSRAPI-0083 row is excluded rather than treated as actionable.
        .filter((row) => !actionFilterOn || row.ops?.needsAttention === true)
    );
  }, [pageRows, actionFilterOn]);

  const pageNumber = (identityPage?.page ?? page) + 1;
  const totalPages = identityPage?.totalPages ?? 0;
  const hasLoadedRows = (identityPage?.content.length ?? 0) > 0;
  const emptyMessage =
    hasLoadedRows && actionFilterOn
      ? t('tenants.emptyStatus', 'No tenants on this page match the selected filters.')
      : query
        ? t('tenants.emptySearch', 'No tenants match your search.')
        : t('tenants.emptyBody', 'No tenants found.');

  const handleToggleGlobal = async (enabled: boolean) => {
    setGlobalError(null);
    try {
      await toggleGlobal(enabled);
    } catch {
      setGlobalError(t('global.toggleError', 'Could not update global setting. Please try again.'));
    }
  };

  // WSRPDM-0083: wsr-api mirrors the tenant into the ops roster as part of the
  // create, so both rosters refresh — otherwise the new row reads "Ops not provisioned".
  const handleCreated = (tenant: TenantDto) => {
    setCreateOpen(false);
    setPage(0);
    refetchIdentity();
    refetchOps();
    setCreateNote(t('tenants.create.success', 'Tenant “{{name}}” created.', { name: tenant.name }));
  };

  // TENANT_CREATE_OUTCOME_UNKNOWN: refresh both rosters so the admin can check
  // for the slug; the dialog stays open with its warning and typed values.
  const handleCreateOutcomeUnknown = () => {
    setPage(0);
    refetchIdentity();
    refetchOps();
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    setPage(0);
  };

  const handleStatusFilterChange = (value: StatusFilter) => {
    setStatusFilter(value);
    setPage(0);
  };

  return (
    <Box>
      <Typography variant="h1" component="h1" gutterBottom>
        {t('page.tenants.title', 'Tenants')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('page.tenants.subtitle', 'Tenant roster and configuration.')}
      </Typography>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{
          mb: 2,
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small"
            value={searchInput}
            onChange={(event) => handleSearchChange(event.target.value)}
            label={t('tenants.searchLabel', 'Search tenants')}
            placeholder={t('tenants.searchPlaceholder', 'Search tenant or slug')}
            sx={{ minWidth: 260 }}
          />
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel id="tenant-status-filter-label">
              {t('tenants.statusFilter.label', 'Status')}
            </InputLabel>
            <Select
              labelId="tenant-status-filter-label"
              value={statusFilter}
              label={t('tenants.statusFilter.label', 'Status')}
              onChange={(event) => handleStatusFilterChange(event.target.value as StatusFilter)}
            >
              <MenuItem value="ALL">{t('tenants.statusFilter.all', 'All')}</MenuItem>
              <MenuItem value="ACTIVE">{t('tenants.statusFilter.active', 'Active')}</MenuItem>
              <MenuItem value="SUSPENDED">
                {t('tenants.statusFilter.suspended', 'Suspended')}
              </MenuItem>
            </Select>
          </FormControl>
          {hasNeedsAttentionField ? (
            <Box>
              <AppSwitch
                label={t('tenants.actionFilter.label', 'Needs attention')}
                checked={actionFilterOn}
                onChange={setActionFilterOn}
              />
              {actionFilterOn ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t('tenants.actionFilter.pageOnly', 'Filtering the tenants loaded on this page.')}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Stack>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <AppSwitch
            label={t('global.enableLabel', 'Enable global ingestion')}
            checked={globalEnabled ?? false}
            onChange={(checked) => void handleToggleGlobal(checked)}
            disabled={globalEnabled === null || globalToggling}
          />
          <Button variant="contained" onClick={() => setCreateOpen(true)}>
            {t('tenants.createButton', 'Create tenant')}
          </Button>
        </Stack>
      </Stack>

      {globalError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGlobalError(null)}>
          {globalError}
        </Alert>
      ) : null}

      {createNote ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreateNote(null)}>
          {createNote}
        </Alert>
      ) : null}

      {opsError ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={<Button onClick={refetchOps}>{t('common.retry', 'Retry')}</Button>}
        >
          {t('tenants.opsLoadError', 'Operational details could not be loaded.')}
        </Alert>
      ) : null}

      <TenantsTable
        tenants={rows}
        loading={identityLoading || (opsLoading && opsTenants === null)}
        error={identityError ? t('tenants.loadError', 'Could not load tenants.') : null}
        onRetry={refetchIdentity}
        emptyMessage={emptyMessage}
      />

      <Stack
        direction="row"
        spacing={2}
        sx={{ mt: 2, alignItems: 'center', justifyContent: 'flex-end' }}
      >
        <Button
          variant="outlined"
          disabled={identityLoading || (identityPage?.page ?? page) <= 0}
          onClick={() => setPage((current) => Math.max(0, current - 1))}
        >
          {t('tenants.pagination.previous', 'Previous')}
        </Button>
        <Typography variant="body2" aria-live="polite">
          {t('tenants.pagination.pageOf', 'Page {{page}} of {{total}}', {
            page: totalPages === 0 ? 0 : pageNumber,
            total: totalPages,
          })}
        </Typography>
        <Button
          variant="outlined"
          disabled={
            identityLoading || totalPages === 0 || (identityPage?.page ?? page) + 1 >= totalPages
          }
          onClick={() => setPage((current) => current + 1)}
        >
          {t('tenants.pagination.next', 'Next')}
        </Button>
      </Stack>

      <CreateTenantDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
        onOutcomeUnknown={handleCreateOutcomeUnknown}
      />
    </Box>
  );
}
